import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Component, type ReactNode } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ToastContainer } from './components/Toast';
import { LoginPage } from './pages/LoginPage';
import { UsersPage } from './pages/UsersPage';
import { UserEditPage } from './pages/UserEditPage';
import { StoresPage } from './pages/StoresPage';
import { StoreMembersPage } from './pages/StoreMembersPage';
import { ReportsPage } from './pages/ReportsPage';
import { StatisticsPage } from './pages/StatisticsPage';

// 临时调试：捕获渲染错误，打印对象结构
class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: unknown }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: unknown) {
    console.error('[ErrorBoundary] caught render error:', error);
    if (error && typeof error === 'object') {
      console.error('[ErrorBoundary] error keys:', Object.keys(error as object));
      console.error('[ErrorBoundary] error value:', JSON.stringify(error, null, 2));
    }
    return { error };
  }

  render() {
    if (this.state.error !== null) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="w-full max-w-lg bg-red-50 border border-red-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">页面渲染错误</h2>
            <pre className="text-sm text-red-700 whitespace-pre-wrap break-all">
              {JSON.stringify(this.state.error, null, 2)}
            </pre>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  console.log('[RequireAuth] loading:', loading, 'user:', user, 'user type:', typeof user, 'user keys:', user ? Object.keys(user) : 'null');
  if (loading) {
    console.log('[RequireAuth] → 加载中…');
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        加载中…
      </div>
    );
  }
  if (!user) {
    console.log('[RequireAuth] → 未登录，重定向到 /login');
    return <Navigate to="/login" replace />;
  }
  console.log('[RequireAuth] → 已登录，渲染子组件');
  return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  console.log('[RedirectIfAuthed] loading:', loading, 'user:', user, 'user type:', typeof user, 'user keys:', user ? Object.keys(user) : 'null');
  if (loading) {
    console.log('[RedirectIfAuthed] → 加载中…');
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        加载中…
      </div>
    );
  }
  if (user) {
    console.log('[RedirectIfAuthed] → 已登录，重定向到 /users');
    return <Navigate to="/users" replace />;
  }
  console.log('[RedirectIfAuthed] → 未登录，渲染子组件 (LoginPage)');
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfAuthed>
            <LoginPage />
          </RedirectIfAuthed>
        }
      />
      <Route
        path="/users"
        element={
          <RequireAuth>
            <UsersPage />
          </RequireAuth>
        }
      />
      <Route
        path="/users/:id/edit"
        element={
          <RequireAuth>
            <UserEditPage />
          </RequireAuth>
        }
      />
      <Route
        path="/stores"
        element={
          <RequireAuth>
            <StoresPage />
          </RequireAuth>
        }
      />
      <Route
        path="/stores/:id/members"
        element={
          <RequireAuth>
            <StoreMembersPage />
          </RequireAuth>
        }
      />
      <Route
        path="/reports"
        element={
          <RequireAuth>
            <ReportsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/statistics"
        element={
          <RequireAuth>
            <StatisticsPage />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/users" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <ToastContainer />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
